<template>
  <div class="start">

    <!-- Hero Section -->
    <section class="hero">
      <div class="hero-text">
        <h1>Still-, Schlaf- & Beikostberatung in Koblenz & Online</h1>
        <p>Mit Einfühlungsvermögen und einem offenen Ohr begleite ich euch durch die ersten spannenden Jahre mit Baby und Kleinkind – persönlich in einer 1:1 Beratung, oder unkompliziert in der Gruppe in meinen Kursen.</p>
        <button class="cta-button" @click="$router.push('/kontakt/')">Jetzt Kontakt aufnehmen</button>
      </div>
      <div class="hero-image">
        <img src="@/assets/maike_start.jpg" alt="Maike Röhrig – Familienbegleitung Koblenz" width="300" height="auto"/>
      </div>
    </section>
    
    <section>
      <h2>NEU: kostenlose Onlinesprechstunde!</h2>
      <p>
        Ihr habt nur eine <strong>kurze Frage</strong>? Oder möchtet mich einfach <strong>kennenlernen</strong>, um zu schauen, ob wir auf einer Wellenlänge sind? Ich biete regelmäßig eine Onlinesprechstunde an!
      </p>
      <button class="cta-button" @click="$router.push('/kontakt/')">Teilnahmelink anfragen</button>
    </section>

    <!-- Kurstermine -->
    <section class="termine">
      <h2>Aktuelle Kurse & Termine</h2>

      <p v-if="upcomingEvents.length === 0">
        Aktuell sind keine Kurse geplant. Melde dich gerne, wenn du Interesse hast!
      </p>

      <!-- Desktop Tabelle -->
      <table class="termin-table">
        <thead>
          <tr>
            <th>Datum</th>
            <th>Uhrzeit</th>
            <th>Ort</th>
            <th>Veranstaltung</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="event in upcomingEvents" :key="event.id" :class="{ 'has-icons': event.badge || event.info }" >
            <td>
              <span v-if="event.startDate !== event.endDate">
                {{ formatDate(event.startDate) }} <br> - {{ formatDate(event.endDate) }}
              </span>
              <span v-else>
                {{ formatDate(event.startDate) }}
              </span>
            </td>
            <td>{{ event.startTime }} - {{ event.endTime }} Uhr</td>
            <td>{{ event.location }}</td>
            <td>
                <router-link v-if="event.eventUrlRelative" :to="event.eventUrlRelative">{{ event.name }}</router-link>
                <a v-else-if="event.eventUrlExtern" :href="event.eventUrlExtern" target="_blank" rel="noopener noreferrer">
                    {{ event.name }} 🔗
                </a>
                <span v-else>{{ event.name }}</span>
            </td>
            <td>
                <div v-if="event.badge" class="badge">{{ event.badge }}</div>
                <svg v-if="event.info" class="info-icon"
                     @mouseenter="infoVisibleId = event.id"
                     @mouseleave="infoVisibleId = null"
                     @click.stop="toggleInfo(event.id)"
                     xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                  <circle cx="12" cy="12" r="10" fill="white" stroke="var(--secondary-font-color)" stroke-width="2"/>
                  <text x="12" y="16" text-anchor="middle" fill="var(--secondary-font-color)" font-size="12" font-family="Arial" dy="0">i</text>
                </svg>
                <div v-if="infoVisibleId === event.id" class="info-box">{{ event.info }}</div>
            </td>
          </tr>
        </tbody>
      </table>

      <!-- Mobile Ansicht -->
      <ul class="termin-liste">
        <li
          v-for="event in upcomingEvents"
          :key="event.id"
          class="termin"
          :class="{ 'has-badge': event.badge }"
          @click="handleClick(event)"
        >
          <span class="datum">
              <span v-if="event.startDate !== event.endDate">
              {{ formatDate(event.startDate) }}, {{ event.startTime }} Uhr <br> - {{ formatDate(event.endDate) }}, {{ event.endTime }} Uhr
              </span>
              <span v-else>
              {{ formatDate(event.startDate) }}, {{ event.startTime }} - {{ event.endTime }} Uhr
              </span>
          </span> <br>
          <span class="ort">{{ event.location }}</span> <br>
          <span class="kurs">
              {{ event.name }}
              <span v-if="event.eventUrlExtern" class="extern-symbol">🔗</span>
          </span>
          <div v-if="event.badge" class="badge">{{ event.badge }}</div>
          <svg v-if="event.info" class="info-icon"
               @mouseenter="infoVisibleId = event.id"
               @mouseleave="infoVisibleId = null"
               @click.stop="toggleInfo(event.id)"
               xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
            <circle cx="12" cy="12" r="10" fill="white" stroke="var(--secondary-font-color)" stroke-width="2"/>
            <text x="12" y="16" text-anchor="middle" fill="var(--secondary-font-color)" font-size="12" font-family="Arial" dy="0">i</text>
          </svg>
          <div v-if="infoVisibleId === event.id" class="info-box">{{ event.info }}</div>
        </li>
      </ul>
    </section>

    <!-- Kontakt CTA Section -->
    <section class="kontakt-cta">
      <h2>Ihr habt Fragen oder möchtet euch anmelden?</h2>
      <p>
        <strong>Ich freue mich auf eure Nachricht</strong> – ganz unverbindlich per Mail, WhatsApp, Instagram oder auch telefonisch!
      </p>
      <p>
        Ihr seid <strong>noch unsicher</strong>? Wir können gerne ein kurzes Gespräch per Telefon oder Zoom vereinbaren, um uns kennenzulernen und herauszufinden, ob meine Beratung zu euch und eurem Problem passt!
      </p>
      <p>
        Ihr seid mindestens zu viert und möchtet einen <strong>privaten Kurs für eure Gruppe</strong>? Online oder vielleicht sogar ganz bequem bei euch zu Hause? Kein Problem – wir finden einen Termin und ich komme gerne vorbei!
      </p>
      <button class="cta-button" @click="$router.push('/kontakt/')">Jetzt Kontakt aufnehmen</button>
    </section>

  </div>
</template>


<script>
import events from '@/data/events';

export default {
  name: "Start",
  data() {
    return {
      infoVisibleId: null,
      defaultOrganizer: {
        type: "Person",
        name: "Maikes Familienbegleitung",
        url: "https://maikes-familienbegleitung.de"
      },
      defaultPerformer: {
        name: "Maike Röhrig"
      },
      defaultDescription: "Ein interaktiver Kurs mit praktischen Tipps und individueller Beratung.",
      defaultImage: "https://maikes-familienbegleitung.de/favicon.svg",
      defaultPrice: "30",
      defaultOfferUrl: "https://maikes-familienbegleitung.de/preise",
      defaultAddress: {
        streetAddress: "Trierer Strasse 250a",
        addressLocality: "Koblenz",
        postalCode: "56072",
        addressCountry: "DE"
      }
    };
  },
  mounted() {
    if (this.upcomingEvents.length > 0) {
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.text = JSON.stringify({
        "@context": "https://schema.org",
        "@type": "EventSeries",
        "name": "Kurse und Workshops",
        "event": this.upcomingEvents.map(event => ({
          "@type": "Event",
          "name": event.name,
          "description": event.description || this.defaultDescription,
          "image": event.image || this.defaultImage,
          "startDate": `${event.startDate}T${event.startTime}:00+02:00`,
          "endDate": `${event.endDate}T${event.endTime}:00+02:00`,
          "location": event.isOnline
            ? { "@type": "VirtualLocation", "url": "https://zoom.us" }
            : {
                "@type": "Place",
                "name": event.location,
                "address": event.address || this.defaultAddress
              },
          "eventAttendanceMode": event.isOnline
            ? "https://schema.org/OnlineEventAttendanceMode"
            : "https://schema.org/OfflineEventAttendanceMode",
          "eventStatus": "https://schema.org/EventScheduled",
          "offers": {
            "@type": "Offer",
            "url": event.eventUrlRelative
              ? "https://maikes-familienbegleitung.de" + event.eventUrlRelative
              : event.eventUrlExtern
                  ? event.eventUrlExtern
                  : this.defaultOfferUrl,
            "price": event.price || this.defaultPrice,
            "priceCurrency": "EUR",
            "availability": "https://schema.org/InStock",
            "validFrom": "2025-03-01"
          },
          "organizer": event.organizer || this.defaultOrganizer,
          "performer": event.performer
            ? { "@type": "Person", "name": event.performer }
            : { "@type": "Person", "name": this.defaultPerformer }
        }))
      }, null, 2);
      document.head.appendChild(script);
    }
    window.addEventListener('scroll', this.closeInfoBox);
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.closeInfoBox);
  },
  computed: {
    upcomingEvents() {
      const now = new Date();
      return events.filter(event => {
        const eventDateTime = new Date(`${event.endDate}T${event.endTime}`);
        return eventDateTime >= now;
      });
    }
  },
  methods: {
    formatDate(isoDate) {
      const date = new Date(isoDate);
      return date.toLocaleDateString("de-DE", {
        weekday: "short", // Mo, Di, Mi, etc.
        day: "2-digit",
        month: "2-digit",
        year: "numeric"
      });
    },
    handleClick(event) {
        if (event.eventUrlExtern) {
            window.open(event.eventUrlExtern, '_blank', 'noopener');
        } else if (event.eventUrlRelative) {
            this.$router.push(event.eventUrlRelative);
        }
    },
    toggleInfo(id) {
      this.infoVisibleId = this.infoVisibleId === id ? null : id;
    },
    closeInfoBox() {
      this.infoVisibleId = null;
    }
  }
};
</script>

<style scoped>
.start {
}

.extern-symbol {
  font-size: 0.8em;
  margin-left: 5px;
}

.termin-table {
  width: 100%;
  border-collapse: collapse;
  margin: 20px 0;
}

.termin-table th,
.termin-table td {
  padding: 10px;
  border-bottom: 1px solid #ddd;
}

.termin-table tr.has-icons {
  height: 3rem;
  position: relative;
}

.termin-liste {
  display: none;
}

.info-icon {
  position: absolute;
  bottom: 0.5rem;
  right: 0.5rem;
  width: 20px;
  height: 20px;
  cursor: pointer;
}

.info-box {
  position: absolute;
  bottom: 2rem;
  right: 0.5rem;
  background: white;
  border: 1px solid #ccc;
  border-radius: 6px;
  padding: 8px;
  font-size: 0.85rem;
  box-shadow: 0 2px 4px rgba(0,0,0,0.15);
  z-index: 10;
  max-width: 90%;
  transition: opacity 0.2s ease-in-out;
  opacity: 1;
}

.badge {
  position: absolute;
  top: 0;
  right: 0;
  background: var(--primary-color);
  color: white;
  font-size: 0.75rem;
  padding: 4px 8px;
  border-bottom-left-radius: 8px;
  font-weight: bold;
}

@media (max-width: 45rem) {
  .termin-table {
    display: none;
  }
  .termin-liste {
    display: block;
    list-style: none;
    padding: 0;
    width: 100%;
  }
  .termin {
    position: relative;
    background-color: var(--main-color-2);
    margin: 10px 0;
    padding: 15px;
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    cursor: pointer;
  }
  .termin.has-badge {
    padding-top: 2rem;
  }
  .datum {
    font-weight: bold;
  }
  .ort {
    font-style: italic;
  }
}
</style>